import Class from '@/Class';
import ComponentManager from '@/Management/ComponentManager';
import * as CoreFrameDTOs from "coreframetypings/dto";
import type DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import UIListenerHelper from '@/UIListener/UIListenerHelper';
import Utilities from '@/Utility/Utilities';
import type ListViewViewModel from '@/ViewModels/Views/ListViewViewModel';
import ControlFactory from '@/Factories/ControlFactory';
import type EditContext from '@/ViewModels/Contexts/EditContext';
import type ModalDetailViewContext from '@/Components/ModalDetailView/ModalDetailViewContext';

export default class UIListener extends Class {

    /**
     * Enthält die untergeordneten UIListener der UIListener-Chain
     */
    public subUIListeners = [];

    private static registeredDefaultActions: { [key: string]: ((...params) => any)[] };

    private static registeredDefaultActionsForTypes: {
        [key: string]: { [key: string]: ((...params) => any)[] }
    };

    private registeredActions: { [key: string]: ((...params) => any)[] };

    constructor(params?) {
        super();
        this.isDisposable = true;
    }

    public onBeforeSave(context: DetailViewContext): boolean | JQueryDeferred<boolean> | JQueryPromise<boolean> {
        return true;
    }

    public onBeforeApply(context: DetailViewContext): boolean |JQueryPromise<boolean> {
        return Utilities.resolveImmediately(true);
    }

    public onBeforeCancel(context: DetailViewContext): boolean | JQueryDeferred<boolean> | JQueryPromise<boolean> {
        return true;
    }

    public onAfterDeleteFromListView(context: ListViewViewModel) { }

    private onBeforeApplyInternal(context): JQueryPromise<boolean> {
        return this.noneAsync(this.callActionsAsync('onBeforeApply', [context]), false)
            .then((result) => {
                if (result === false)
                    return result;

                return this.onBeforeApply(context);
            }).promise();
    }

    private onBeforeRenderInternal(context: DetailViewContext): void {
        UIListener.callDefaultActions('onBeforeRender', [context], context.getConcretePdTypeName());

        this.onBeforeRender(context);
    }

    private onBeforeRenderSummaryViewInternal(context: DetailViewContext): void {
        UIListener.callDefaultActions('onBeforeRenderSummaryView', [context], context.getConcretePdTypeName());

        this.onBeforeRenderSummaryView(context);
    }

    public onAfterApply(context: DetailViewContext, isClosing: boolean) { }

    private onPropertyValueUpdatedInternal(context: DetailViewContext, property): void {
        this.callActions('onPropertyValueUpdated', [context, property]);

        this.onPropertyValueUpdated(context, property);
    }

    public onPropertyValueUpdated(context: DetailViewContext, property) {};

    public onDetailViewReadyInternal(context: DetailViewContext) {
        this.callActions('onDetailViewReady', [context]);

        this.onDetailViewReady(context);
    }

    public onDetailViewReady(context: DetailViewContext) { }

    public onDetailViewClosedInternal(context: DetailViewContext) {
        this.callActions('onDetailViewClosed', [context]);

        this.onDetailViewClosed(context);
    }

    public onDetailViewClosed(context: DetailViewContext) { }

    private onAssociationUpdatedInternal(context: DetailViewContext, relation): void {
        this.callActions('onAssociationUpdated', [context, relation]);

        this.onAssociationUpdated(context, relation);
    }

    public onAssociationUpdated(context: DetailViewContext, relation: string) { };

    public onBeforeCallOperation(context: any, operation: string): boolean | JQueryDeferred<boolean>| JQueryPromise<boolean> {
        return true;
    }

    public onCallOperation(context: DetailViewContext, operation: string, args: any): JQueryDeferred<CoreFrameDTOs.IOperationResultDTO> {
        return undefined;
    }

    public onAfterCallOperation(context: DetailViewContext, operation: string, args: CoreFrameDTOs.IOperationResultDTO) { }

    public onBeforeRender(context: DetailViewContext) { }

    /**
     * Wird ausgeführt, wenn eine Assoziation verändert wird. Der Rückgabewert entscheidet ob die Änderung durchgeführt werden soll
     * @param relation
     * @param actionType
     * @param additionalParameters
     */
    public onBeforeAssociationUpdated(context: EditContext, relation: string, actionType: string, additionalParameters: { toPdoIds: Array<string> }): JQueryDeferred<boolean> | JQueryPromise<boolean> {
        return undefined;
    }

    public onBeforeRenderSummaryView(context: DetailViewContext) { }

    public onBeforeListViewRender(context: ListViewViewModel, dto: CoreFrameDTOs.IListViewDTO) {  }

    public onSummaryViewReady(context: DetailViewContext) { }

    public onAfterRefresh(context: DetailViewContext) { }

    public refineContextDto(context: DetailViewContext) { }

    public onGenericObjectUpdated(context: EditContext, updatedProperties: Record<string, any>) { }

    public onListViewReady(context: ListViewViewModel) { }

    public onPartialRendered(context: DetailViewContext) { }

    /**
     * Erstellt eine UIListener-Chain
     */
    public createChain(...chain: Array<string | { uiListener: string, params: Object }>) {
        for (let i = 0; i < chain.length; i++) {
            let uiListener = null;
            let params = {};
            let argument = chain[i];

            if (typeof argument === 'string') {
                uiListener = argument;
            }
            else if (typeof argument === 'object' && argument.hasOwnProperty('uiListener') && argument.hasOwnProperty('params')) {
                if (typeof argument.uiListener === 'string' && typeof argument.params === 'object') {
                    uiListener = argument.uiListener;
                    params = argument.params;
                }
            }

            if (uiListener) {
                let thisSubUIListener = UIListener.createInstance(uiListener, params);
                this.subUIListeners.push(thisSubUIListener);
            }
        }

        this.onBeforeSave = (context) => {
            return this.noneAsync(_.invoke(this.subUIListeners, 'onBeforeSave', context), false);
        };

        this.onBeforeApply = (context) => {
            return this.noneAsync(_.invoke(this.subUIListeners, 'onBeforeApply', context), false).promise();
        };

        this.onAfterApply = (context, isClosing) => {
            _.invoke(this.subUIListeners, 'onAfterApply', context, isClosing);
        };

        this.onAfterDeleteFromListView = (context) => {
            _.invoke(this.subUIListeners, 'onAfterDeleteFromListView', context);
        };

        this.onBeforeCancel = (context) => {
            return this.noneAsync(_.invoke(this.subUIListeners, 'onBeforeCancel', context), false);
        };

        this.onPropertyValueUpdated = (context, property) => {
            _.invoke(this.subUIListeners, 'onPropertyValueUpdated', context, property);
        };

        this.onDetailViewReady = (context) => {
            _.invoke(this.subUIListeners, 'onDetailViewReady', context);
        };

        this.onDetailViewClosed = (context) => {
            _.invoke(this.subUIListeners, 'onDetailViewClosed', context);
        };

        this.onAssociationUpdated = (context, relation) => {
            _.invoke(this.subUIListeners, 'onAssociationUpdated', context, relation);
        };

        this.onBeforeAssociationUpdated = (context, relation, actionType, additionalParameters) => {
            return this.noneAsync(_.invoke(this.subUIListeners, 'onBeforeAssociationUpdated', context, relation, actionType, additionalParameters), false);
        };

        this.onBeforeCallOperation = (context, operation) => {
            return this.noneAsync(_.invoke(this.subUIListeners, 'onBeforeCallOperation', context, operation), false);
        };

        /* Kann nicht als Chain realisiert werden, sondern nur überschrieben werden!
            this.onCallOperation = (context, operation, args) => {
                return _.invoke(this.subUIListeners, 'onCallOperation', context, operation, args);
            };
        */

        this.onAfterCallOperation = (context, operation, args) => {
            _.invoke(this.subUIListeners, 'onAfterCallOperation', context, operation, args);
        };

        this.onAfterRefresh = (context) => {
            _.invoke(this.subUIListeners, 'onAfterRefresh', context);
        };

        this.refineContextDto = (context) => {
            _.invoke(this.subUIListeners, 'refineContextDto', context);
        }

        this.onGenericObjectUpdated = (context, updatedProperties) => {
            _.invoke(this.subUIListeners, 'onGenericObjectUpdated', context, updatedProperties);
        };

        this.onBeforeRender = (context) => {
            _.invoke(this.subUIListeners, 'onBeforeRender', context);
        };

        this.onBeforeListViewRender = (context, dto) => {
            _.invoke(this.subUIListeners, 'onBeforeListViewRender', context, dto);
        };

        this.onListViewReady = (context) => {
            _.invoke(this.subUIListeners, 'onListViewReady', context);
        };

        this.onPartialRendered = (context) => {
            _.invoke(this.subUIListeners, 'onPartialRendered', context);
        };

        this.onBeforeRenderSummaryView = (context) => {
            _.invoke(this.subUIListeners, 'onBeforeRenderSummaryView', context);
        };

        this.onSummaryViewReady = (context) => {
            _.invoke(this.subUIListeners, 'onSummaryViewReady', context);
        };
    }

    public isDetailViewContext(context: DetailViewContext | ListViewViewModel): context is DetailViewContext {
        return UIListenerHelper.isDetailViewContext(context);
    }

    public isModalDetailViewContext(context: DetailViewContext | ListViewViewModel): context is ModalDetailViewContext {
        return UIListenerHelper.isModalDetailViewContext(context);
    }

    public isListViewContext(context: DetailViewContext | ListViewViewModel): context is ListViewViewModel {
        return UIListenerHelper.isListViewContext(context);
    }

    /**
     * Hilfsfunktion: Gibt "false" zurück, falls einer der Werte in der Liste dem Parameter "value" entspricht; ansonsten true
     * @param list
     * @param value
     */
    private none(list, value): boolean {
        let result = true;
        _.each(list, (val) => {
            if (val === value) {
                result = false;
            }
        });

        return result;
    }

    private noneAsync(list, value): JQueryDeferred<boolean> {
        let self = this;
        const deferred = $.Deferred();
        $.when.apply($, list).then(function () {
            if (self.isDisposed) {
                deferred.reject();
            }
            for (let i = 0; i < arguments.length; i++) {
                if (arguments[i] === value) {
                    deferred.resolve(false);
                    return;
                }
            }
            deferred.resolve(true);
        }, () => {
            deferred.resolve(false);
        });

        return deferred;
    }

    /**
     * Liefert alle DetailViewGroups des übergebenen DetailViewContexts
     * @param context
     * @param recursive Sollen die Gruppen rekursiv ermittelt werden, oder nur die oberste Ebene
     */
    public getDetailViewGroups(context: DetailViewContext, recursive: boolean = true): CoreFrameDTOs.IDetailViewGroupDTO[] {
        const dvc = context as DetailViewContext;

        if (Utilities.isNullOrUndefined(dvc) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO) ||
            Utilities.isNullOrUndefined(dvc.detailViewContextDTO.detailView))
            return [];

        return this.getDetailViewGroupsInDetailView(dvc.detailViewContextDTO.detailView, recursive);
    }

    /**
     * Hilsfunktion zum Ermitteln aller DetailViewGroups einer DetailView
     * @param detailView
     * @param recursive
     */
    private getDetailViewGroupsInDetailView(detailView: CoreFrameDTOs.IDetailViewDTO, recursive: boolean = true): CoreFrameDTOs.IDetailViewGroupDTO[] {
        let groups = detailView.detailViewGroups;

        if (recursive) {
            groups.forEach((group) => {
                const subgroups = this.getDetailViewGroupsInDetailViewGroup(group, recursive);
                groups = groups.concat(subgroups);
            });
        }

        return groups;
    }

    /**
     * Hilsfunktion zum Ermitteln aller DetailViewGroups einer DetailViewGroup
     * @param detailViewGroup
     * @param recursive
     */
    private getDetailViewGroupsInDetailViewGroup(detailViewGroup: CoreFrameDTOs.IDetailViewGroupDTO, recursive: boolean = true): CoreFrameDTOs.IDetailViewGroupDTO[] {
        let groups = detailViewGroup.detailViewGroups;

        if (recursive) {
            groups.forEach((group) => {
                const subgroups = this.getDetailViewGroupsInDetailViewGroup(group, recursive);
                groups = groups.concat(subgroups);
            });
        }

        return groups;
    }

    /**
     * Entfernt ein IDVGItemDTO anhand des Namens
     * @param context
     * @param name
     */
    public removeDetailViewItemDto(context: DetailViewContext, name: string): void {
        UIListenerHelper.removeDetailViewItemDto(context, name);
    }


    /**
     * Entfernt ein IDetailViewGroupDTO anhand des Namens
     * @param context
     * @param name
     */
    public removeDetailViewGroupDto(context: DetailViewContext, name: string): void {
        UIListenerHelper.removeDetailViewGroupDto(context, name);
    }

    /**
     * Liefert ein IDetailViewGroupDTO anhand des Namens
     * @param context
     * @param name
     */
    public getDetailViewGroupDto(context: DetailViewContext, name: string): CoreFrameDTOs.IDetailViewGroupDTO {
        return UIListenerHelper.getDetailViewGroupDto(context, name);
    }

    /**
     * Liefert ein IDVGItemDTO anhand des Namens
     * @param context
     * @param name
     */
    public getDetailViewItemDto(context: DetailViewContext, name: string): CoreFrameDTOs.IDVGItemDTO {
        return UIListenerHelper.getDetailViewItemDto(context, name);
    }

    /**
     * Liefert alle ControlFactories innerhalb einer Gruppe zurück
     * @param context
     * @param dvGroupName
     * @param subGroupsInCluded
     */
    public getUiFactoriesInDetailViewGroup(context: DetailViewContext, dvGroupName: string, subGroupsInCluded: boolean): ControlFactory[] {
        return UIListenerHelper.getUiFactoriesInDetailViewGroup(context, dvGroupName, subGroupsInCluded);
    }

    /**
     * Aktiviert alle ControlFactories innerhalb einer Gruppe
     * @param context
     * @param dvGroupName
     * @param subGroupsInCluded
     */
    public enableUiFactoriesInDetailViewGroup(context: DetailViewContext, dvGroupName: string, subGroupsInCluded: boolean): void {
        UIListenerHelper.enableUiFactoriesInDetailViewGroup(context, dvGroupName, subGroupsInCluded);
    }

    /**
     * Deaktiviert alle ControlFactories innerhalb einer Gruppe
     * @param context
     * @param dvGroupName
     * @param subGroupsInCluded
     */
    public disableUiFactoriesInDetailViewGroup(context: DetailViewContext, dvGroupName: string, subGroupsInCluded: boolean): void {
        UIListenerHelper.disableUiFactoriesInDetailViewGroup(context, dvGroupName, subGroupsInCluded);
    }

    /**
     * Ruft registrierte Actions für einen UIListener asynchron auf
     * @param name
     * @param params
     */
    private callActionsAsync(name: string, params: any[]): JQueryPromise<boolean>[] {
        const registeredActions = this.getActions(name);
        return registeredActions.map(action => action.apply(action, params));
    }

    /**
     * Ruft registrierte Actions für einen UIListener auf
     * @param name
     * @param params
     */
    private callActions(name: string, params: any[]): void {
        const registeredActions = this.getActions(name);
        registeredActions.map(action => action.apply(action, params));
    }

    /**
     * Ruft registrierte Default-Actions für einen UIListener asynchron auf
     * @param name
     * @param params
     * @param typeName
     */
    private static callDefaultActionsAsync(name: string, params: any[], typeName: string): JQueryPromise<boolean>[] {
        const registeredDefaultActions = UIListener.getDefaultActions(name, typeName);
        return registeredDefaultActions.map(action => action.apply(action, params));
    }

    /**
     * Ruft registrierte Default-Actions für einen UIListener auf
     * @param name
     * @param params
     * @param typeName
     */
    private static callDefaultActions(name: string, params: any[], typeName: string): void {
        const registeredDefaultActions = UIListener.getDefaultActions(name, typeName);
        registeredDefaultActions.map(action => action.apply(action, params));
    }

    /**
     * Liefert die registrierten Aktionen für einen UIListener für einen Typen
     * @param name
     */
    private static getDefaultActions(name: string, typeName: string): ((...params) => any)[] {
        let result = [];
        if (!Utilities.isNullOrUndefined(UIListener.registeredDefaultActions) && !Utilities.isNullOrUndefined(UIListener.registeredDefaultActions[name]))
            result = result.concat(UIListener.registeredDefaultActions[name]);

        if (!Utilities.isNullOrUndefined(UIListener.registeredDefaultActionsForTypes) && !Utilities.isNullOrUndefined(UIListener.registeredDefaultActionsForTypes[typeName]) && !Utilities.isNullOrUndefined(UIListener.registeredDefaultActionsForTypes[typeName][name]))
            result = result.concat(UIListener.registeredDefaultActionsForTypes[typeName][name]);

        return result;
    }

    /**
     * Liefert die registrierten Aktionen für einen UIListener
     * @param name
     */
    private getActions(name: string): ((...params) => any)[] {
        if(Utilities.isNullOrUndefined(this.registeredActions) || Utilities.isNullOrUndefined(this.registeredActions[name]))
            return [];

        return this.registeredActions[name];
    }

    /**
     * Registriert eine Aktion für einen UIListener
     * @param name
     * @param action
     */
    private registerAction(name: string, action: (...params) => any): void {
        if (this.registeredActions == null)
            this.registeredActions = {};

        if (this.registeredActions[name] == null)
            this.registeredActions[name] = [];

        this.registeredActions[name].push(action);
    }

    /**
     * Registriert eine Default-Aktion für alle UIListener (Typeinschränkung ist möglich
     * @param name
     * @param action
     * @param typeName
     */
    private static registerDefaultAction(name: string, action: (...params) => any, typeNames?: string[]): void {
        if (Utilities.isNullOrUndefinedOrBlank(typeNames) || typeNames.length === 0) {
            if (UIListener.registeredDefaultActions == null)
                UIListener.registeredDefaultActions = {};

            if (UIListener.registeredDefaultActions[name] == null)
                UIListener.registeredDefaultActions[name] = [];

            UIListener.registeredDefaultActions[name].push(action);
        } else {
            for (let typeName of typeNames) {
                if (UIListener.registeredDefaultActionsForTypes == null)
                    UIListener.registeredDefaultActionsForTypes = {};

                if (UIListener.registeredDefaultActionsForTypes[typeName] == null)
                    UIListener.registeredDefaultActionsForTypes[typeName] = {};

                if (UIListener.registeredDefaultActionsForTypes[typeName][name] == null)
                    UIListener.registeredDefaultActionsForTypes[typeName][name] = [];

                UIListener.registeredDefaultActionsForTypes[name][typeName].push(action);
            }
        }
    }

    /**
     * Registriert eine Default-Aktion, die beim UILIstener "onBeforeRender" ausgeführt wird (Typeinschränkung möglich)
     * @param action
     * @param typeNames
     */
    public static registerDefaultOnBeforeRenderAction(action: (context: DetailViewContext) => void, typeNames?: string[]): void {
        UIListener.registerDefaultAction('onBeforeRender', action, typeNames);
    }

    /**
     * Registriert eine Default-Aktion, die beim UILIstener "onBeforeRenderSummaryView" ausgeführt wird (Typeinschränkung möglich)
     * @param action
     * @param typeNames
     */
    public static registerDefaultOnBeforeRenderSummaryViewAction(action: (context: DetailViewContext) => void, typeNames?: string[]): void {
        UIListener.registerDefaultAction('onBeforeRenderSummaryView', action, typeNames);
    }

    /**
     * Registriert eine Aktion, die beim UILIstener "onBeforeApply" ausgeführt wird
     * @param action
     */
    public registerOnBeforeApplyAction(action: (context: DetailViewContext) => JQueryPromise<boolean>): void {
        this.registerAction('onBeforeApply', action);
    }

    /**
     * Registriert eine Aktion, die beim UILIstener "onDetailViewReady" ausgeführt wird
     * @param action
     */
    public registerOnDetailViewReadyAction(action: (context: DetailViewContext) => void): void {
        this.registerAction('onDetailViewReady', action);
    }

    /**
    * Registriert eine Aktion, die beim UILIstener "onAssociationUpdated" ausgeführt wird
    * @param action
    */
    public registerOnAssociationUpdatedAction(action: (context: DetailViewContext, relation) => void): void {
        this.registerAction('onAssociationUpdated', action);
    }

    /**
    * Registriert eine Aktion, die beim UILIstener "onPropertyValueUpdated" ausgeführt wird
    * @param action
    */
    public registerOnPropertyValueUpdatedAction(action: (context: DetailViewContext, property) => void): void {
        this.registerAction('onPropertyValueUpdated', action);
    }

    /**
    * Registriert eine Aktion, die beim UILIstener "onDetailViewClosed" ausgeführt wird
    * @param action
    */
    public registerOnDetailViewClosedAction(action: (context: DetailViewContext) => void): void {
        this.registerAction('onDetailViewClosed', action);
    }

    /**
     * Disposed den UIListener
     */
    protected disposeInternal() : void {
        for (let i = 0; i < this.subUIListeners.length; i++) {
            this.subUIListeners[i].dispose();
        }

        this.subUIListeners = [];

        super.disposeInternal();
    }

    /**
     * Erzeugt eine neue Instanz eines UIListeners anhand einer Komponentenbezeichnung
     * @param component
     * @param params
     */
    public static createInstance(component: string, params?: Object): UIListener {
        if (!params) {
            params = {};
        }
        let Component = ComponentManager.getUIListener<typeof UIListener>(component || 'DefaultUIListener');

        if (Component == null) {
            Component = ComponentManager.getUIListener<typeof UIListener>('DefaultUIListener');
            console.log(`UIListener "${component}" wurde nicht gefunden. Fallback "DefaultUIListener" wird verwendet."`);
        }

        return new Component(params);
    }

}